import React, { useEffect, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";

const COLORS = ["#dc2626", "#2563eb", "#16a34a", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#3f3f46"];
const MODES = [["buyer", "Buyer overall"], ["vendor", "By affiliate"], ["funnel", "By funnel"]];
const WEEKS = [[8, "8 weeks"], [12, "12 weeks"], [26, "26 weeks"]];
const fmtWeek = (w) => new Date(`${w}T00:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });

function toRows(data) {
  return data.weeks.map((w, i) => {
    const row = { week: fmtWeek(w) };
    data.lines.forEach((l, j) => {
      const p = l.points[i];
      row[`s${j}`] = p.rate == null ? null : Number((p.rate * 100).toFixed(1));
      row[`s${j}_n`] = `${p.rejected} of ${p.sent}`;
    });
    return row;
  });
}

function TipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-black bg-white p-2 text-xs">
      <div className="label-mono mb-1">Week of {label}</div>
      {payload.filter((p) => p.value != null).map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>{p.name}: <b>{p.value}%</b> ({p.payload[`${p.dataKey}_n`]} rejected)</div>
      ))}
    </div>
  );
}

function Toggle({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
                className={`border border-black px-2.5 py-1 text-[11px] uppercase tracking-widest transition-colors duration-100 ${value === v ? "bg-black text-white" : "hover:bg-zinc-100"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function Chart({ data }) {
  const flag = (data.threshold?.rate ?? 0.2) * 100;
  return (
    <div className="h-72 w-full" data-testid="buyer-rej-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={toRows(data)} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
          <CartesianGrid stroke="#e4e4e7" vertical={false} />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis unit="%" tick={{ fontSize: 11 }} domain={[0, (max) => Math.max(40, Math.ceil(max / 10) * 10)]} />
          <Tooltip content={<TipBox />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceLine y={flag} stroke="#dc2626" strokeDasharray="4 4"
                         label={{ value: `${flag}% flag`, fontSize: 10, fill: "#dc2626", position: "insideTopRight" }} />
          {data.lines.map((l, j) => (
            <Line key={l.id} type="monotone" dataKey={`s${j}`} name={l.name} stroke={COLORS[j % COLORS.length]}
                  strokeWidth={2} dot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Week-by-week rejection rate for the buyer chosen in the report (or every buyer).
export function RejectionTrendChart({ buyerId }) {
  const [by, setBy] = useState("buyer");
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/_admin/analytics/buyer-rejections/trend", { params: { by, weeks, ...(buyerId ? { buyer_id: buyerId } : {}) } })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [by, weeks, buyerId]);
  return (
    <section className="brut-card mb-4 p-4" data-testid="buyer-rej-trend">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-chivo text-lg font-black">Week by week</div>
          <div className="text-[11px] text-zinc-600">Rejection rate each week, so you can see when traffic started going bad.</div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Toggle options={MODES} value={by} onChange={setBy} testId="buyer-rej-trend-by" />
          <Toggle options={WEEKS} value={weeks} onChange={setWeeks} testId="buyer-rej-trend-weeks" />
        </div>
      </div>
      {!data && <div className="label-mono p-6 text-center">Loading…</div>}
      {data?.error && <div className="text-xs text-red-700" data-testid="buyer-rej-trend-error">Couldn&apos;t load the weekly trend.</div>}
      {data && !data.error && (data.lines.length ? <Chart data={data} /> : (
        <div className="label-mono p-6 text-center" data-testid="buyer-rej-trend-empty">No leads sent to buyers in these weeks.</div>
      ))}
      {data?.more > 0 && (
        <p className="mt-2 text-[11px] text-zinc-600" data-testid="buyer-rej-trend-more">
          Showing the {data.lines.length} busiest lines; {data.more} quieter {data.more === 1 ? "one is" : "ones are"} hidden. Pick a buyer above to narrow it down.
        </p>
      )}
    </section>
  );
}
